import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { caseStudies } from "../data/caseStudies.js";
import Eyebrow from "./Eyebrow.jsx";
import ScreenMedia from "./ScreenMedia.jsx";
import { refreshOnHeightChange } from "../scrollRefresh.js";
import usePrefersReducedMotion, { prefersReducedMotion } from "../hooks/usePrefersReducedMotion.js";
import CountUp from "./CountUp.jsx";
import "./OurWorks.css";

gsap.registerPlugin(ScrollTrigger);

/**
 * "Our works": one full-screen card per case study, modelled on the
 * "Pin Rotate Sections" reference.
 *
 * - Every card but the last pins at the top until the last card arrives
 *   (pinSpacing: false, so the next card slides up over it).
 * - While the next card rises, the pinned one shrinks to 0.75, tilts ±5° /
 *   rotationX ±40° (alternating) and an overlay darkens it to 0.4.
 * - Each work has its own layout; its micro-interactions play once the card
 *   is mostly in view and reset when scrolled back above it.
 */

const pad = (n) => String(n).padStart(2, "0");

/* ------------------------------------------------------------------ */
/* Fit to screen                                                       */
/* ------------------------------------------------------------------ */

/*
 * Every card is exactly one screen tall, but its content is sized from a mix of rem, vw and vh, so on
 * a short window (a 13" laptop with a toolbar) or with a larger browser font size it runs past the
 * bottom, and a pinned card can't be scrolled to reveal the rest. Such a card is laid out as if the
 * screen were bigger and scaled down to fit, like zooming the browser out: same design, just smaller.
 * Cards that already fit are left alone.
 */
const MIN_FIT = 0.5;

/** Bottom of a card's in-flow content in layout px (transforms ignored), and the room that pieces anchored to the card's bottom (the marquee) take up. */
function measureContent(layout) {
  const height = layout.offsetHeight;
  let bottom = 0;
  let reserved = 0;
  for (const el of layout.querySelectorAll("*")) {
    if (!(el instanceof HTMLElement) || !el.offsetHeight) continue;
    let top = 0;
    let node = el;
    while (node && node !== layout) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    if (!node) continue; // fixed, or not laid out inside the card
    const cs = getComputedStyle(el);
    if (cs.position === "absolute" && cs.bottom !== "auto" && cs.top === "auto") {
      if (el.offsetParent === layout) reserved = Math.max(reserved, height - top);
      continue;
    }
    bottom = Math.max(bottom, top + el.offsetHeight);
  }
  return { bottom, reserved };
}

function fitCard(card, enabled) {
  const fit = card.querySelector(".ow-fit");
  const layout = fit?.querySelector(".ow-layout");
  if (!layout) return;
  fit.classList.remove("is-fit");
  fit.style.removeProperty("--ow-fit");
  if (!enabled) return;

  const screen = card.clientHeight;
  const margin = Math.min(28, Math.max(12, screen * 0.025));
  const fits = (m, s) => m.bottom * s + Math.max(margin, m.reserved * s) <= screen + 0.5;

  let m = measureContent(layout);
  if (fits(m, 1)) return;
  // Zoomed out the layout is wider, so it wraps less (and image heights grow with their width):
  // measure again at each step until it really fits
  let s = 1;
  for (let i = 0; i < 5; i++) {
    s = Math.max(MIN_FIT, Math.min(s, screen / (m.bottom + Math.max(margin / s, m.reserved))) - 0.003);
    fit.classList.add("is-fit");
    fit.style.setProperty("--ow-fit", s.toFixed(4));
    m = measureContent(layout);
    if (s <= MIN_FIT || fits(m, s)) break;
  }
}

/** The zoom-out factor of the card holding `el` (1 when it fits), for turning pointer pixels into layout px. */
const fitScaleOf = (el) => parseFloat(el.closest(".ow-fit")?.style.getPropertyValue("--ow-fit")) || 1;

/* ------------------------------------------------------------------ */
/* Shared hooks + pieces                                               */
/* ------------------------------------------------------------------ */

/**
 * Builds a paused timeline for a layout and plays it when the card is ~60%
 * in view; scrolling back above the card rewinds it so it replays next time.
 */
function useStage(ref, build) {
  const [active, setActive] = useState(false);
  const buildRef = useRef(build);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = prefersReducedMotion();
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ paused: true });
      buildRef.current?.(tl, el);
      if (reduced) {
        tl.progress(1);
        setActive(true);
        return;
      }
      ScrollTrigger.create({
        trigger: el,
        start: "top 40%",
        onEnter: () => {
          tl.restart();
          setActive(true);
        },
        onLeaveBack: () => {
          tl.pause(0);
          setActive(false);
        },
      });
    }, el);
    return () => ctx.revert();
  }, [ref]);

  return active;
}

function Arrow() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="ow-arrow">
      <path d="M3.333 8h9.334M10 10.667 12.667 8M10 5.333 12.667 8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CaseLink({ slug, label = "View case study", className = "" }) {
  return (
    <Link to={`/work/${slug}`} className={`ow-link ${className}`}>
      <span className="ow-link-text">{label}</span>
      <span className="ow-link-icon">
        <Arrow />
      </span>
    </Link>
  );
}

function CardHead({ index, study }) {
  return (
    <>
      <div className="ow-head" data-ow-reveal="">
        <span className="ow-index" aria-hidden="true">
          <span className="ow-index-roll">({pad(index + 1)})</span>
        </span>
        <span className="ow-meta">
          {study.tagPill} <i aria-hidden="true">·</i> {study.year}
        </span>
      </div>
    </>
  );
}

/*
 * Brings elements in from a start pose to the pose CSS gives them. It is a fromTo() with the end spelled
 * out, not from(): from() reads the resting pose off the element, and on elements that have a CSS
 * transition on transform (.ow-feature, .ow-check, .ow-phone) that read can catch a mid-transition
 * value, leaving the tween to end where it started (the check cards stayed 40px to the right, the
 * features 18px low, the phone tilted and sunk).
 */
const REST = { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 };
function enter(tl, targets, vars, at) {
  const els = gsap.utils.toArray(targets);
  const naturalOpacity = new Map(els.map((el) => [el, parseFloat(getComputedStyle(el).opacity)]));
  const from = {};
  const to = {};
  for (const [key, value] of Object.entries(vars)) {
    if (key in REST) {
      from[key] = value;
      to[key] = key === "opacity" ? (_, el) => naturalOpacity.get(el) : REST[key];
    } else to[key] = value;
  }
  return tl.fromTo(els, from, to, at);
}

const revealUp = (tl, el, at = 0) =>
  enter(tl, el.querySelectorAll("[data-ow-reveal]"), { y: 28, opacity: 0, duration: 0.9, stagger: 0.08, ease: "expo.out" }, at);

const CHANNEL_ICONS = {
  facebook: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="6" fill="#1877f2" />
      <path d="M13.4 19.5v-6.3h2.1l.3-2.5h-2.4V9.2c0-.7.2-1.2 1.2-1.2h1.3V5.8c-.2 0-1-.1-1.9-.1-1.9 0-3.1 1.1-3.1 3.2v1.8H8.8v2.5h2.1v6.3z" fill="#fff" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id="ow-ig" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#feda75" />
          <stop offset="0.35" stopColor="#fa7e1e" />
          <stop offset="0.6" stopColor="#d62976" />
          <stop offset="1" stopColor="#4f5bd5" />
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="6" fill="url(#ow-ig)" />
      <rect x="6" y="6" width="12" height="12" rx="3.6" fill="none" stroke="#fff" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="2.9" fill="none" stroke="#fff" strokeWidth="1.5" />
      <circle cx="15.5" cy="8.5" r="0.9" fill="#fff" />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="6" fill="#25d366" />
      <path transform="translate(4.2 4.2) scale(0.65)" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" fill="#fff" />
    </svg>
  ),
};
// WooCommerce (WordPress shops) and Shopify: the website side of an online store (marks from simple-icons, CC0)
CHANNEL_ICONS.woocommerce = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect width="24" height="24" rx="6" fill="#96588a" />
    <path transform="translate(2.4 2.1) scale(0.8)" d="M.754 9.58a.754.754 0 00-.754.758v2.525c0 .42.339.758.758.758h3.135l1.431.799-.326-.799h2.373a.757.757 0 00.758-.758v-2.525a.757.757 0 00-.758-.758H.754zm2.709.445h.03c.065.001.124.023.179.067a.26.26 0 01.103.19.29.29 0 01-.033.16c-.13.239-.236.64-.322 1.199-.083.541-.114.965-.094 1.267a.392.392 0 01-.039.219.213.213 0 01-.176.12c-.086.006-.177-.034-.263-.124-.31-.316-.555-.788-.735-1.416-.216.425-.375.744-.478.957-.196.376-.363.568-.502.578-.09.007-.166-.069-.233-.228-.17-.436-.352-1.277-.548-2.524a.297.297 0 01.054-.222c.047-.064.116-.095.21-.102.169-.013.265.065.288.238.103.695.217 1.284.336 1.766l.727-1.387c.066-.126.15-.192.25-.199.146-.01.237.083.273.28.083.441.188.817.315 1.136.086-.844.233-1.453.44-1.828a.255.255 0 01.218-.147zm1.293.36c.056 0 .116.006.18.02.232.05.411.177.53.386.107.18.161.395.161.654 0 .343-.087.654-.26.94-.2.332-.459.5-.781.5a.88.88 0 01-.18-.022.763.763 0 01-.531-.384 1.287 1.287 0 01-.158-.659c0-.342.085-.655.258-.937.202-.333.462-.498.78-.498zm2.084 0c.056 0 .116.006.18.02.236.05.411.177.53.386.107.18.16.395.16.654 0 .343-.086.654-.259.94-.2.332-.459.5-.781.5a.88.88 0 01-.18-.022.763.763 0 01-.531-.384 1.287 1.287 0 01-.16-.659c0-.342.087-.655.26-.937.202-.333.462-.498.78-.498zm4.437.047c-.305 0-.546.102-.718.304-.173.203-.256.49-.256.856 0 .395.086.697.256.906.17.21.418.316.744.316.315 0 .559-.107.728-.316.17-.21.256-.504.256-.883s-.087-.673-.26-.879c-.176-.202-.424-.304-.75-.304zm-1.466.002a1.13 1.13 0 00-.84.326c-.223.22-.332.499-.332.838 0 .362.108.658.328.88.22.223.505.336.861.336.103 0 .22-.016.346-.052v-.54c-.117.034-.216.051-.303.051a.545.545 0 01-.422-.177c-.106-.12-.16-.278-.16-.48 0-.19.053-.348.156-.468a.498.498 0 01.397-.181c.103 0 .212.015.332.049v-.537a1.394 1.394 0 00-.363-.045zm12.414 0a1.135 1.135 0 00-.84.326c-.223.22-.332.499-.332.838 0 .362.108.658.328.88.22.223.506.336.861.336.103 0 .22-.016.346-.052v-.54c-.116.034-.216.051-.303.051a.545.545 0 01-.422-.177c-.106-.12-.16-.278-.16-.48 0-.19.053-.348.156-.468a.498.498 0 01.397-.181c.103 0 .212.015.332.049v-.537a1.394 1.394 0 00-.363-.045zm-9.598.06l-.29 2.264h.579l.156-1.559.395 1.559h.412l.379-1.555.164 1.555h.603l-.304-2.264h-.791l-.12.508c-.03.13-.06.264-.087.4l-.067.352a29.97 29.97 0 00-.258-1.26h-.771zm2.768 0l-.29 2.264h.579l.156-1.559.396 1.559h.412l.375-1.555.165 1.555h.603l-.305-2.264h-.789l-.119.508c-.03.13-.06.264-.086.4l-.066.352c-.063-.352-.15-.771-.26-1.26h-.771zm3.988 0v2.264h.611v-1.031h.012l.494 1.03h.645l-.489-1.019a.61.61 0 00.37-.552.598.598 0 00-.25-.506c-.167-.123-.394-.186-.68-.186h-.713zm3.377 0v2.264H24v-.483h-.63v-.414h.54v-.468h-.54v-.416h.626v-.483H22.76zm-4.793.004v2.264h1.24v-.483h-.627v-.416h.541v-.468h-.54v-.415h.622v-.482h-1.236zm2.025.432c.146.003.25.025.313.072.063.046.091.12.091.227 0 .156-.135.236-.404.24v-.54zm-15.22.011c-.104 0-.205.069-.301.211a1.078 1.078 0 00-.2.639c0 .096.02.2.06.303.049.13.117.198.196.215.083.016.173-.02.27-.106.123-.11.205-.273.252-.492.016-.077.023-.16.023-.246 0-.097-.02-.2-.06-.303-.05-.13-.116-.198-.196-.215a.246.246 0 00-.045-.006zm2.083 0c-.103 0-.204.069-.3.211a1.078 1.078 0 00-.2.639c0 .096.02.2.06.303.049.13.117.198.196.215.083.016.173-.02.27-.106.123-.11.205-.273.252-.492.013-.077.023-.16.023-.246 0-.097-.02-.2-.06-.303-.05-.13-.116-.198-.196-.215a.246.246 0 00-.045-.006zm4.428.006c.233 0 .354.218.354.66-.004.273-.038.46-.098.553a.293.293 0 01-.262.139.266.266 0 01-.242-.139c-.056-.093-.084-.28-.084-.562 0-.436.11-.65.332-.65Z" fill="#fff" />
  </svg>
);
CHANNEL_ICONS.shopify = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect width="24" height="24" rx="6" fill="#7ab55c" />
    <path transform="translate(4.2 3.9) scale(0.65)" d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01zM11.17.83c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z" fill="#fff" />
  </svg>
);
const CHANNELS = ["facebook", "instagram", "whatsapp", "woocommerce", "shopify"];

/* ------------------------------------------------------------------ */
/* Omni-Channel: channels converge into one inbox                 */
/* ------------------------------------------------------------------ */

function OmniLayout({ study, index }) {
  const ref = useRef(null);
  const run = useStage(ref, (tl, el) => {
    revealUp(tl, el);
    enter(tl, el.querySelector(".ow-browser"), { y: 60, opacity: 0, duration: 1.1, ease: "expo.out" }, 0.1);
    // the channel icons arrive, glide into the middle and fold into one Version3 icon
    const apps = el.querySelectorAll(".ow-omni-app");
    const merged = el.querySelector(".ow-omni-merged");
    const spread = (i) => (i - (CHANNELS.length - 1) / 2) * 84;
    tl.fromTo(apps, { x: spread, y: 14, scale: 0.9, opacity: 0 }, { y: 0, scale: 1, opacity: 1, duration: 0.6, stagger: 0.09, ease: "expo.out" }, 0.3);
    tl.to(apps, { x: 0, scale: 0.72, duration: 0.75, ease: "power3.inOut" }, 1.3);
    // a short blur hides the crossfade, so it reads as one icon becoming another
    tl.to(apps, { opacity: 0, filter: "blur(6px)", duration: 0.25, ease: "power2.in" }, 1.8);
    tl.fromTo(merged.querySelector(".ow-omni-v3"), { scale: 0.7, opacity: 0, filter: "blur(6px)" }, { scale: 1, opacity: 1, filter: "blur(0px)", duration: 0.7, ease: "back.out(1.7)" }, 1.85);
    tl.fromTo(merged.querySelectorAll(".ow-omni-badge"), { scale: 0.6, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.45, stagger: 0.07, ease: "back.out(2.2)" }, 2.2);
    tl.fromTo(merged.querySelector(".ow-omni-caption"), { x: -10, opacity: 0 }, { x: 0, opacity: 1, duration: 0.6, ease: "expo.out" }, 2.3);
  });

  return (
    <div ref={ref} className="ow-layout ow-omni">
      <div className="ow-omni-text">
        <CardHead index={index} study={study} />
        <h3 className="ow-title" data-ow-reveal="">{study.title}</h3>
        <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
        <p className="ow-body" data-ow-reveal="">{study.solution.split(". ")[0]}.</p>
        <div className="ow-stats" data-ow-reveal="">
          {study.stats.map((s) => (
            <div className="ow-stat" key={s.label}>
              <div className="ow-stat-num"><CountUp value={s.number} run={run} /></div>
              <div className="ow-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
        <div data-ow-reveal=""><CaseLink slug={study.slug} /></div>
      </div>
      <div className="ow-omni-visual">
        <div className="ow-omni-channels" aria-hidden="true">
          {CHANNELS.map((c) => (
            <span key={c} className="ow-omni-app">{CHANNEL_ICONS[c]}</span>
          ))}
          <span className="ow-omni-merged">
            <span className="ow-omni-v3">
              <img src="/assets/logo/version3_icon_b.svg" alt="" />
              <span className="ow-omni-badges">
                {CHANNELS.map((c) => (
                  <span key={c} className="ow-omni-badge">{CHANNEL_ICONS[c]}</span>
                ))}
              </span>
            </span>
            <span className="ow-omni-caption">
              <b>One inbox</b>
              <small>every channel, one team</small>
            </span>
          </span>
        </div>
        <div className="ow-browser ow-hover-zoom">
          <div className="ow-browser-bar">
            <i /><i /><i />
            <span className="ow-browser-url">{study.liveUrl?.replace(/^https?:\/\//, "")}</span>
          </div>
          <div className="ow-media"><ScreenMedia src={study.heroImage} alt={`${study.title} interface`} /></div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ERRUM: editorial grid, cursor-follow "View" pill, marquee      */
/* ------------------------------------------------------------------ */

function ErrumLayout({ study, index }) {
  const ref = useRef(null);
  const pillRef = useRef(null);
  const run = useStage(ref, (tl, el) => {
    revealUp(tl, el);
    tl.fromTo(
      el.querySelectorAll(".ow-errum-img"),
      { clipPath: "inset(100% 0% 0% 0%)" },
      { clipPath: "inset(0% 0% 0% 0%)", duration: 1.2, stagger: 0.12, ease: "expo.inOut" },
      0
    );
  });

  // "View" pill trails the pointer over the image grid
  useEffect(() => {
    const grid = ref.current?.querySelector(".ow-errum-grid");
    const pill = pillRef.current;
    if (!grid || !pill || !window.matchMedia("(hover: hover)").matches) return;
    gsap.set(pill, { xPercent: -50, yPercent: -50, scale: 0, opacity: 0 });
    const xTo = gsap.quickTo(pill, "x", { duration: 0.45, ease: "power3" });
    const yTo = gsap.quickTo(pill, "y", { duration: 0.45, ease: "power3" });
    const move = (e) => {
      const r = grid.getBoundingClientRect();
      const k = fitScaleOf(grid);
      xTo((e.clientX - r.left) / k);
      yTo((e.clientY - r.top) / k);
    };
    const enter = () => gsap.to(pill, { scale: 1, opacity: 1, duration: 0.35, ease: "back.out(2)" });
    const leave = () => gsap.to(pill, { scale: 0, opacity: 0, duration: 0.25, ease: "power2.in" });
    grid.addEventListener("pointermove", move);
    grid.addEventListener("pointerenter", enter);
    grid.addEventListener("pointerleave", leave);
    return () => {
      grid.removeEventListener("pointermove", move);
      grid.removeEventListener("pointerenter", enter);
      grid.removeEventListener("pointerleave", leave);
      gsap.killTweensOf(pill);
    };
  }, []);

  const [brand, ...rest] = study.title.split(" ");
  const ticker = study.stats.map((s) => `${s.number} ${s.label}`);

  return (
    <div ref={ref} className={`ow-layout ow-errum${run ? " is-run" : ""}`}>
      <div className="ow-errum-top">
        <CardHead index={index} study={study} />
        <h3 className="ow-errum-title" data-ow-reveal="">
          {brand} <em>{rest.join(" ")}</em>
        </h3>
      </div>
      <Link to={`/work/${study.slug}`} className="ow-errum-grid ow-errum-grid--single" aria-label={`View ${study.title}`}>
        <div className="ow-errum-img ow-errum-img--hero ow-media">
          <ScreenMedia src={study.cardScreens?.[0] ?? study.heroImage} alt={`${study.title} home page`} />
        </div>
        <span ref={pillRef} className="ow-errum-pill" aria-hidden="true">View</span>
      </Link>
      <div className="ow-errum-bottom">
        <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
        <div data-ow-reveal=""><CaseLink slug={study.slug} /></div>
      </div>
      <div className="ow-marquee" aria-hidden="true">
        <div className="ow-marquee-track">
          {[0, 1].map((k) => (
            <span key={k} className="ow-marquee-group">
              {ticker.map((t) => (
                <span key={t} className="ow-marquee-item">{t}<i>✦</i></span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hiring suite: 8-stage pipeline fills, pointer-tilt frame        */
/* ------------------------------------------------------------------ */

// what changed for the team, from the case study's results
const SHIFTS = [
  ["CV screening", "hours of reading", "minutes"],
  ["Interview booking", "email threads", "self-booked"],
  ["Review bar", "differed per reviewer", "one rubric"],
];

function HiringLayout({ study, index }) {
  const ref = useRef(null);
  const tiltRef = useRef(null);
  useStage(ref, (tl, el) => {
    revealUp(tl, el);
    enter(tl, el.querySelector(".ow-tilt"), { x: -60, opacity: 0, duration: 1.1, ease: "expo.out" }, 0);
    enter(tl, el.querySelectorAll(".ow-shift li"), { y: 14, opacity: 0, duration: 0.7, stagger: 0.12, ease: "expo.out" }, 0.45);
    enter(tl, el.querySelectorAll(".ow-feature"), { y: 18, opacity: 0, duration: 0.8, stagger: 0.1, ease: "expo.out" }, 0.5);
    tl.fromTo(el.querySelector(".ow-hiring-score b"), { textContent: 0 }, { textContent: 92, snap: { textContent: 1 }, duration: 2, ease: "power2.out" }, 0.5);
    tl.fromTo(el.querySelector(".ow-ring-val"), { strokeDashoffset: 100 }, { strokeDashoffset: 8, duration: 2, ease: "power2.out" }, 0.5);
    enter(tl, el.querySelector(".ow-hiring-score"), { y: 30, opacity: 0, duration: 0.9, ease: "expo.out" }, 0.35);
    enter(tl, el.querySelector(".ow-hiring-thumb"), { y: -30, opacity: 0, duration: 0.9, ease: "expo.out" }, 0.45);
    enter(tl, el.querySelector(".ow-hiring-chip"), { x: 40, opacity: 0, duration: 0.8, ease: "back.out(1.6)" }, 1.4);
  });

  useEffect(() => {
    const frame = tiltRef.current;
    if (!frame || !window.matchMedia("(hover: hover)").matches) return;
    // The pointer is read against the wrapper, which never rotates. Reading it against the frame
    // itself made every tilt move the frame's own box under the cursor, which fed back into the
    // tilt (and flipped :hover on and off at the edges): the visible flashing and jitter.
    const wrap = frame.parentElement;
    const rx = gsap.quickTo(frame, "rotationX", { duration: 0.6, ease: "power3" });
    const ry = gsap.quickTo(frame, "rotationY", { duration: 0.6, ease: "power3" });
    const move = (e) => {
      const r = wrap.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      ry((px - 0.5) * 12);
      rx(-(py - 0.5) * 10);
      // light sheen follows the pointer across the screenshot
      frame.style.setProperty("--mx", `${px * 100}%`);
      frame.style.setProperty("--my", `${py * 100}%`);
    };
    const leave = () => {
      rx(0);
      ry(0);
      frame.style.removeProperty("--mx");
      frame.style.removeProperty("--my");
    };
    wrap.addEventListener("pointermove", move);
    wrap.addEventListener("pointerleave", leave);
    return () => {
      wrap.removeEventListener("pointermove", move);
      wrap.removeEventListener("pointerleave", leave);
    };
  }, []);

  return (
    <div ref={ref} className="ow-layout ow-hiring">
      <div className="ow-hiring-visual">
        <div ref={tiltRef} className="ow-tilt">
          <div className="ow-media ow-tilt-img">
            <ScreenMedia src={study.heroImage} alt={`${study.title} interface`} />
            <span className="ow-tilt-sheen" aria-hidden="true" />
          </div>
          <div className="ow-hiring-score">
            <svg className="ow-ring" viewBox="0 0 40 40" aria-hidden="true">
              <circle className="ow-ring-track" cx="20" cy="20" r="16" pathLength="100" />
              <circle className="ow-ring-val" cx="20" cy="20" r="16" pathLength="100" />
            </svg>
            <div>
              <span>AI CV score</span>
              <b>92</b>
              <small>/100 · learns from your past hires</small>
            </div>
          </div>
          <div className="ow-media ow-hiring-thumb"><ScreenMedia src={study.galleryImages[0]} alt="" /></div>
          <div className="ow-hiring-chip" aria-hidden="true">
            <i className="ow-live-dot" />
            <div>
              <b>New applicant</b>
              <small>graded the moment it lands</small>
            </div>
          </div>
        </div>
      </div>
      <div className="ow-hiring-text">
        <CardHead index={index} study={study} />
        <h3 className="ow-title" data-ow-reveal="">{study.title}</h3>
        <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
        <ul className="ow-shift" data-ow-reveal="">
          {SHIFTS.map(([what, from, to]) => (
            <li key={what}>
              <span>{what}</span>
              <s>{from}</s>
              <i aria-hidden="true">→</i>
              <b>{to}</b>
            </li>
          ))}
        </ul>
        <div className="ow-features" data-ow-reveal="">
          {study.stats.slice(1).map((s, i) => (
            <div key={s.label} className="ow-feature">
              <span className="ow-feature-icon" aria-hidden="true">
                {i === 0 ? (
                  <svg viewBox="0 0 20 20"><path d="M10 2.5v3M10 14.5v3M2.5 10h3M14.5 10h3M4.7 4.7l2.1 2.1M13.2 13.2l2.1 2.1M4.7 15.3l2.1-2.1M13.2 6.8l2.1-2.1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                ) : (
                  <svg viewBox="0 0 20 20"><rect x="3" y="4" width="14" height="13" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M3 8h14M7 2.5v3M13 2.5v3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                )}
              </span>
              <b>
                {s.number} {s.label}
              </b>
              <small>{s.description}</small>
            </div>
          ))}
        </div>
        <div data-ow-reveal=""><CaseLink slug={study.slug} /></div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AI agent: live chat cycling English / Bangla / Banglish         */
/* ------------------------------------------------------------------ */

// each chat also carries what the agent did to answer it (shown in the trace card beside the phone)
const CHATS = [
  {
    lang: "English", q: "Do you have the black panjabi in L?", a: "Yes! 4 left in L. Want me to reserve one for you?",
    trace: { lang: "English", skill: "product_search", tool: 'check_availability("L")', result: "4 in stock" },
  },
  {
    lang: "বাংলা", q: "আমার অর্ডারটা এখন কোথায়?", a: "আপনার অর্ডার ঢাকায় পৌঁছেছে — আজই ডেলিভারি হবে।",
    trace: { lang: "Bangla", skill: "order_tracking", tool: "lookup_orders(phone)", result: "Out for delivery" },
  },
  {
    lang: "Banglish", q: "Return policy ta ki bhai?", a: "7 diner moddhe easy return — kono jhamela nai!",
    trace: { lang: "Banglish", skill: "general", tool: 'search_policies("return")', result: "Policy found" },
  },
];

function AgentLayout({ study, index }) {
  const ref = useRef(null);
  const [chat, setChat] = useState(0);
  const run = useStage(ref, (tl, el) => {
    revealUp(tl, el);
    enter(tl, el.querySelector(".ow-chat"), { y: 80, rotation: 3, opacity: 0, duration: 1.2, ease: "expo.out" }, 0.1);
    enter(tl, el.querySelectorAll(".ow-agent-float"), { y: 24, opacity: 0, duration: 0.9, stagger: 0.12, ease: "expo.out" }, 0.5);
  });

  useEffect(() => {
    if (!run || prefersReducedMotion()) return;
    const id = setInterval(() => setChat((c) => (c + 1) % CHATS.length), 5200);
    return () => clearInterval(id);
  }, [run]);

  const c = CHATS[chat];

  return (
    <div ref={ref} className="ow-layout ow-agent">
      <div className="ow-agent-text">
        <CardHead index={index} study={study} />
        <h3 className="ow-title" data-ow-reveal="">{study.title}</h3>
        <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
        <div className="ow-agent-stats" data-ow-reveal="">
          {study.stats.map((s) => (
            <div className="ow-agent-stat" key={s.label}>
              <span className="ow-agent-stat-num">{s.number}</span>
              <span className="ow-agent-stat-desc">{s.description}</span>
            </div>
          ))}
        </div>
        <div data-ow-reveal=""><CaseLink slug={study.slug} /></div>
      </div>
      <div className="ow-agent-visual">
        <div className="ow-agent-float ow-agent-handoff" aria-hidden="true">
          <span className="ow-agent-handoff-av">S</span>
          <span>
            <b>Hands over to a person</b>
            <small>with a summary, when it should</small>
          </span>
        </div>
        <div className="ow-chat">
          <div className="ow-chat-head">
            <span className="ow-chat-avatar"><img src="/assets/logo/version3_icon_b.svg" alt="" /></span>
            <div>
              <b>Version3 Agent</b>
              <small><i className="ow-live-dot" /> online · replies instantly</small>
            </div>
          </div>
          <div className="ow-chat-langs" role="tablist" aria-label="Conversation language">
            {CHATS.map((x, i) => (
              <button
                type="button"
                role="tab"
                aria-selected={i === chat}
                key={x.lang}
                className={`ow-chat-lang${i === chat ? " is-on" : ""}`}
                onClick={() => setChat(i)}
              >
                {x.lang}
              </button>
            ))}
          </div>
          <div className="ow-chat-body" key={run ? chat : "idle"}>
            <div className="ow-media ow-chat-product"><ScreenMedia src={study.cardScreens?.[0] ?? study.heroImage} alt="" /></div>
            <p className="ow-bubble ow-bubble--me">{c.q}</p>
            <span className="ow-typing" aria-hidden="true"><i /><i /><i /></span>
            <p className="ow-bubble ow-bubble--bot">{c.a}</p>
          </div>
        </div>
        <div className="ow-agent-float ow-agent-trace" aria-hidden="true">
          <span className="ow-agent-float-h"><i className="ow-live-dot" /> Agent trace</span>
          <ol key={run ? chat : "idle"}>
            <li><span>Language</span><b>{c.trace.lang}</b></li>
            <li><span>Skill</span><code>{c.trace.skill}</code></li>
            <li><span>Tool</span><code>{c.trace.tool}</code></li>
            <li className="is-result"><span>Result</span><b>{c.trace.result}</b></li>
          </ol>
        </div>
        <div className="ow-agent-float ow-agent-stores" aria-hidden="true">
          <span className="ow-agent-float-h">Plugs into the store</span>
          <span className="ow-agent-store-row"><i>WooCommerce</i><i>Shopify</i><i className="is-on">Custom API</i></span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AdLead: dashboard tiles, scan line, magnetic CTA                */
/* ------------------------------------------------------------------ */

function AdLeadLayout({ study, index }) {
  const ref = useRef(null);
  const magRef = useRef(null);
  const run = useStage(ref, (tl, el) => {
    revealUp(tl, el);
    enter(tl, el.querySelectorAll(".ow-dash-cell"), { y: 50, opacity: 0, duration: 1, stagger: 0.08, ease: "expo.out" }, 0.1);
  });

  useEffect(() => {
    const btn = magRef.current;
    if (!btn || !window.matchMedia("(hover: hover)").matches) return;
    const xTo = gsap.quickTo(btn, "x", { duration: 0.5, ease: "elastic.out(1, 0.4)" });
    const yTo = gsap.quickTo(btn, "y", { duration: 0.5, ease: "elastic.out(1, 0.4)" });
    const move = (e) => {
      const r = btn.getBoundingClientRect();
      xTo((e.clientX - (r.left + r.width / 2)) * 0.35);
      yTo((e.clientY - (r.top + r.height / 2)) * 0.35);
    };
    const leave = () => {
      xTo(0);
      yTo(0);
    };
    btn.addEventListener("pointermove", move);
    btn.addEventListener("pointerleave", leave);
    return () => {
      btn.removeEventListener("pointermove", move);
      btn.removeEventListener("pointerleave", leave);
    };
  }, []);

  return (
    <div ref={ref} className={`ow-layout ow-adlead${run ? " is-run" : ""}`}>
      <div className="ow-adlead-top">
        <div>
          <CardHead index={index} study={study} />
          <h3 className="ow-title" data-ow-reveal="">{study.title}</h3>
        </div>
        <div className="ow-adlead-intro">
          <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
          <div data-ow-reveal="">
            <span ref={magRef} className="ow-magnet">
              <CaseLink slug={study.slug} className="ow-link--solid" />
            </span>
          </div>
        </div>
      </div>
      <div className="ow-dash ow-dash--flow">
        {/* the whole product in one strip: keyword, ad library, scored advertisers, delivered leads */}
        <div className="ow-dash-cell ow-dash-flow ow-media">
          <ScreenMedia src={study.cardScreens?.[0] ?? study.heroImage} alt={`How ${study.title} works, from keyword to leads`} />
        </div>
        {study.stats.map((s) => (
          <div className="ow-dash-cell ow-dash-tile" key={s.label}>
            <span className="ow-dash-num">{s.number}</span>
            <span className="ow-dash-tlabel">{s.label}</span>
            <span className="ow-dash-desc">{s.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ListenR: live alert, severity meter, image reticle              */
/* ------------------------------------------------------------------ */

function ListenLayout({ study, index }) {
  const ref = useRef(null);
  const scopeRef = useRef(null);
  const reticleRef = useRef(null);
  useStage(ref, (tl, el) => {
    revealUp(tl, el);
    enter(tl, scopeRef.current, { scale: 0.92, opacity: 0, duration: 1.1, ease: "expo.out" }, 0);
    enter(tl, el.querySelectorAll(".ow-corner"), { scale: 2.2, opacity: 0, duration: 0.7, stagger: 0.06, ease: "expo.out" }, 0.3);
    // one deliberate sweep finds the issues, then the idle loop takes over
    tl.fromTo(el.querySelector(".ow-scan"), { yPercent: 0, opacity: 1 }, { yPercent: 100, duration: 1.4, ease: "power2.inOut" }, 0.55);
    tl.to(el.querySelector(".ow-scan"), { opacity: 0, duration: 0.3 }, 1.95);
    tl.fromTo(el.querySelector(".ow-meter-fill"), { scaleX: 0 }, { scaleX: 0.84, duration: 1.4, ease: "power3.out" }, 0.5);
    enter(tl, el.querySelector(".ow-toast"), { x: 60, opacity: 0, duration: 0.8, ease: "back.out(1.6)" }, 1.7);
  });

  useEffect(() => {
    const scope = scopeRef.current;
    const ret = reticleRef.current;
    if (!scope || !ret || !window.matchMedia("(hover: hover)").matches) return;
    gsap.set(ret, { xPercent: -50, yPercent: -50 });
    const xTo = gsap.quickTo(ret, "left", { duration: 0.35, ease: "power3" });
    const yTo = gsap.quickTo(ret, "top", { duration: 0.35, ease: "power3" });
    const move = (e) => {
      const r = scope.getBoundingClientRect();
      const k = fitScaleOf(scope);
      xTo((e.clientX - r.left) / k);
      yTo((e.clientY - r.top) / k);
    };
    scope.addEventListener("pointermove", move);
    return () => scope.removeEventListener("pointermove", move);
  }, []);

  return (
    <div ref={ref} className="ow-layout ow-listen">
      <div className="ow-listen-visual">
        <div ref={scopeRef} className="ow-scope">
          <div className="ow-media">
            <ScreenMedia src={study.cardScreens?.[0] ?? study.heroImage} alt={`${study.title} interface`} />
            <span className="ow-scan-track" aria-hidden="true"><span className="ow-scan" /></span>
          </div>
          <span className="ow-corner ow-corner--tl" /><span className="ow-corner ow-corner--tr" />
          <span className="ow-corner ow-corner--bl" /><span className="ow-corner ow-corner--br" />
          <span ref={reticleRef} className="ow-reticle" aria-hidden="true">
            <span className="ow-reticle-label">reading image…</span>
          </span>
          <div className="ow-toast" role="status">
            <span className="ow-toast-sev">High</span>
            <div>
              <b>New post flagged</b>
              <small>Fake promo screenshot · 2 min ago</small>
            </div>
          </div>
        </div>
      </div>
      <div className="ow-listen-text">
        <span className="ow-alert" data-ow-reveal="">
          <i className="ow-alert-dot" /> Live monitoring
        </span>
        <CardHead index={index} study={study} />
        <h3 className="ow-title" data-ow-reveal="">{study.title}</h3>
        <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
        <div className="ow-meter" data-ow-reveal="">
          <div className="ow-meter-head">
            <span>Threat severity</span>
            <b>High</b>
          </div>
          <div className="ow-meter-track"><div className="ow-meter-fill" /></div>
          <div className="ow-meter-scale"><span>Low</span><span>Medium</span><span>High</span></div>
        </div>
        <ul className="ow-list" data-ow-reveal="">
          {study.stats.map((s) => (
            <li key={s.label}>
              <strong>{s.number}</strong> {s.label}
            </li>
          ))}
        </ul>
        <div data-ow-reveal=""><CaseLink slug={study.slug} /></div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rahiee: phone mockup, 3-point check-in ticks                    */
/* ------------------------------------------------------------------ */

const CHECKS = [
  { label: "Right face", note: "Matched to profile" },
  { label: "Right uniform", note: "Uniform detected" },
  { label: "Right place", note: "Inside site geofence" },
];

function RahieeLayout({ study, index }) {
  const ref = useRef(null);
  useStage(ref, (tl, el) => {
    revealUp(tl, el);
    // explicit start and end: the phone has a CSS transform transition, and a from() tween that reads
    // its "end" from the computed transform can pick up a mid-transition value and finish where it started
    tl.fromTo(el.querySelector(".ow-phone"), { y: 120, rotation: -4, opacity: 0 }, { y: 0, rotation: 0, opacity: 1, duration: 1.3, ease: "expo.out" }, 0);
    el.querySelectorAll(".ow-check").forEach((check, i) => {
      const at = 0.7 + i * 0.45;
      enter(tl, check, { x: 40, opacity: 0, duration: 0.6, ease: "expo.out" }, at);
      tl.fromTo(check.querySelector(".ow-check-path"), { strokeDashoffset: 24 }, { strokeDashoffset: 0, duration: 0.4, ease: "power2.out" }, at + 0.25);
      tl.fromTo(check.querySelector(".ow-check-ring"), { scale: 0.6 }, { scale: 1, duration: 0.5, ease: "back.out(3)" }, at + 0.2);
    });
  });

  return (
    <div ref={ref} className="ow-layout ow-rahiee">
      <div className="ow-rahiee-text">
        <CardHead index={index} study={study} />
        <h3 className="ow-title" data-ow-reveal="">{study.title}</h3>
        <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
        <p className="ow-body" data-ow-reveal="">{study.challenge.split(". ")[0]}.</p>
        <div data-ow-reveal=""><CaseLink slug={study.slug} label="See how it works" /></div>
      </div>
      <div className="ow-rahiee-phone-wrap">
        <div className="ow-phone">
          <span className="ow-phone-notch" />
          <div className="ow-media ow-phone-screen"><ScreenMedia src={study.cardScreens?.[0] ?? study.cardImage} alt={`${study.title} app`} /></div>
        </div>
      </div>
      <ul className="ow-checks">
        {CHECKS.map((c) => (
          <li className="ow-check" key={c.label} tabIndex={0}>
            <svg viewBox="0 0 24 24" className="ow-check-icon" aria-hidden="true">
              <circle className="ow-check-ring" cx="12" cy="12" r="11" />
              <path className="ow-check-path" d="M7 12.5l3.2 3.2L17 9" pathLength="24" />
            </svg>
            <div>
              <b>{c.label}</b>
              <small>{c.note}</small>
            </div>
          </li>
        ))}
        <li className="ow-check-foot">
          {study.stats.slice(1).map((s) => (
            <span key={s.label}><b>{s.number}</b> {s.label}</span>
          ))}
        </li>
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fallback for any new case study                                     */
/* ------------------------------------------------------------------ */

function DefaultLayout({ study, index }) {
  const ref = useRef(null);
  const run = useStage(ref, (tl, el) => {
    revealUp(tl, el);
    enter(tl, el.querySelector(".ow-media"), { y: 60, opacity: 0, duration: 1.1, ease: "expo.out" }, 0.1);
  });
  return (
    <div ref={ref} className="ow-layout ow-omni">
      <div className="ow-omni-text">
        <CardHead index={index} study={study} />
        <h3 className="ow-title" data-ow-reveal="">{study.title}</h3>
        <p className="ow-tagline" data-ow-reveal="">{study.tagline}</p>
        <div className="ow-stats" data-ow-reveal="">
          {study.stats.map((s) => (
            <div className="ow-stat" key={s.label}>
              <div className="ow-stat-num"><CountUp value={s.number} run={run} /></div>
              <div className="ow-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
        <div data-ow-reveal=""><CaseLink slug={study.slug} /></div>
      </div>
      <div className="ow-omni-visual">
        <div className="ow-media ow-hover-zoom ow-default-img"><ScreenMedia src={study.heroImage} alt={study.title} /></div>
      </div>
    </div>
  );
}

// Card tone alternates by position (light, dark, light, ...), so reordering the
// case studies keeps the rhythm; each layout's CSS reads the tone's variables.
const LAYOUTS = {
  "version3-omni-channel": OmniLayout,
  "errum-online-store": ErrumLayout,
  "version3-website-hiring-suite": HiringLayout,
  "version3-ai-support-agent": AgentLayout,
  "adlead-analyzer": AdLeadLayout,
  "listenr-for-bkash": ListenLayout,
  "rahiee-ai": RahieeLayout,
};

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

export default function OurWorks() {
  const rootRef = useRef(null);
  const reduced = usePrefersReducedMotion();

  useLayoutEffect(() => {
    if (reduced) return;
    const root = rootRef.current;
    if (!root) return;

    const ctx = gsap.context(() => {
      const pinCards = gsap.utils.toArray("[data-ow-card]", root);
      const last = pinCards[pinCards.length - 1];

      // A card is "idle" until it nears the viewport and again once the next card has covered it.
      // Idle cards freeze their CSS loops (see .is-idle in OurWorks.css) and tell their embedded
      // screens to pause (public/work/_shared/screen.js), so only the card being looked at animates.
      const setIdle = (card, idle) => {
        card.classList.toggle("is-idle", idle);
        card.querySelectorAll("iframe").forEach((frame) => frame.contentWindow?.postMessage("v3-screen-sync", location.origin));
      };
      pinCards.forEach((card, index) => {
        card.classList.add("is-idle");
        ScrollTrigger.create({
          trigger: card,
          start: "top bottom",
          endTrigger: pinCards[index + 1] ?? root.nextElementSibling ?? card,
          // covered: the next card's top is within 2% of the viewport top
          end: "top 2%",
          onToggle: (self) => setIdle(card, !self.isActive),
        });
      });

      pinCards.forEach((eachCard, index) => {
        if (index >= pinCards.length - 1) return;

        ScrollTrigger.create({
          trigger: eachCard,
          start: "top top",
          endTrigger: last,
          end: "top top",
          pin: true,
          pinSpacing: false,
        });

        const overlay = eachCard.querySelector(".ow-overlay");
        const tilt = index % 2 === 0 ? 1 : -1;
        const setScale = gsap.quickSetter(eachCard, "scale");
        const setRotation = gsap.quickSetter(eachCard, "rotation");
        const setRotationX = gsap.quickSetter(eachCard, "rotationX");
        const setOverlay = gsap.quickSetter(overlay, "opacity");
        ScrollTrigger.create({
          trigger: pinCards[index + 1],
          start: "top bottom",
          end: "top top",
          onUpdate: (self) => {
            const progress = self.progress;
            setScale(1 - progress * 0.25);
            setRotation(tilt * progress * 5);
            setRotationX(tilt * progress * 40);
            setOverlay(progress * 0.4);
          },
        });
      });

      // Outro: the last card holds (through its .ow-hold runway), then dims, its content drifting up and away, while
      // the next section rises over it as a rounded sheet and its content settles in.
      // (no scaling: a shrunk card would reveal the earlier cards around its edges)
      const next = root.nextElementSibling;
      if (last && next) {
        ScrollTrigger.create({
          trigger: last,
          start: "top top",
          endTrigger: next,
          end: "top top",
          pin: true,
          pinSpacing: false,
        });
        gsap
          .timeline({
            scrollTrigger: { trigger: next, start: "top bottom", end: "top top", scrub: true },
            defaults: { ease: "none" },
          })
          .to(last.querySelector(".ow-layout"), { yPercent: -12, opacity: 0.4 }, 0)
          .to(last.querySelector(".ow-overlay"), { opacity: 0.7 }, 0)
          .fromTo(next.firstElementChild, { y: 140, opacity: 0.2 }, { y: 0, opacity: 1 }, 0);
      }
    }, root);

    // sections above (hero frame, method rail) change height while scrolling,
    // which would leave the pins' start/end stale
    const unwatch = refreshOnHeightChange(root.parentElement);

    return () => {
      unwatch();
      ctx.revert();
    };
  }, [reduced]);

  // zoom a card out when its content would run past the screen (see "Fit to screen")
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cards = Array.from(root.querySelectorAll("[data-ow-card]"));
    const desktop = window.matchMedia("(min-width: 992px)");
    const fitAll = () => cards.forEach((card) => fitCard(card, !reduced && desktop.matches));
    fitAll();
    let timer;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(fitAll, 150);
    };
    window.addEventListener("resize", onResize);
    // text metrics change once the web fonts arrive
    document.fonts?.ready.then(fitAll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
      cards.forEach((card) => fitCard(card, false));
    };
  }, [reduced]);

  return (
    <section ref={rootRef} className={`ow${reduced ? " ow--static" : ""}`} id="work" data-nav-theme="dark">
      <span id="project-section" className="ow-anchor" aria-hidden="true" />

      {caseStudies.map((study, index) => {
        const Component = LAYOUTS[study.slug] ?? DefaultLayout;
        const tone = index % 2 ? "dark" : "light";
        return (
          <Fragment key={study.slug}>
            <article
              data-ow-card=""
              data-nav-theme={tone}
              className={`ow-card ow-card--${tone}${index === 0 ? " ow-card--first" : ""}`}
              aria-label={`${pad(index + 1)} · ${study.title}`}
            >
              <div className="ow-overlay" />
              <div className="ow-fit">
                {index === 0 && (
                  <div className="ow-eyebrow-wrap">
                    <Eyebrow section="work" className="ow-eyebrow" />
                  </div>
                )}
                <Component study={study} index={index} />
              </div>
            </article>
            {/* empty scroll runway: the pinned card above holds still while this
                passes beneath it, before the next card (or, after the last card,
                the next section) rises over it */}
            <div className="ow-hold" aria-hidden="true" />
          </Fragment>
        );
      })}

    </section>
  );
}
